import { format, isSameDay, isThisYear, isToday, isYesterday } from 'date-fns'

import { EMPTY_STRING } from '../../constants/common'
import {
  DATE_DAY_MONTH_FORMAT,
  DATE_DAY_MONTH_YEAR_FORMAT,
  DATE_DAY_MONTH_YEAR_TIME_FORMAT,
} from '../../constants/date'
import { getItemsFromApiResponse } from '../../utils/api'
import { toUkTimezoneDate } from '../../utils/date'
import { getUserId } from '../../utils/user'

import {
  CHATROOM_INPUT_INSERT_REPLACE_OPTION_VALUE,
  CHATROOM_SEND_PERMISSIONS_DISABLED,
} from './Chatroom.constants'

export function isChatroomInputInsertReplaceOptionValueInsert(value) {
  return value === CHATROOM_INPUT_INSERT_REPLACE_OPTION_VALUE.Insert
}

// `?get_prev=1` returns messages newest-first; flip to oldest → newest so the
// local list stays in chronological order.
export function getChatroomMessagesFromGetPrevResponse(response) {
  return [...getItemsFromApiResponse(response)].reverse()
}

export function getChatroomFromResponse(response) {
  return response?.data || null
}

export function getChatroomUnviewedCount(chatroom) {
  return chatroom?.unviewed_msg_count || 0
}

export function getChatroomRealtimeChannel(chatroom) {
  return chatroom?.realtime_public_channel || null
}

export function getIsChatroomCreator({ chatroom, currentUser }) {
  return chatroom?.owner === getUserId(currentUser)
}

// `visibleToDwellers` is undefined for dwellers — only staff should see the notification.
export function getIsChatroomVisibilityBlocked({
  isPublic,
  visibleToDwellers,
}) {
  return isPublic && visibleToDwellers === false
}

export function getChatroomSendPermissions({
  chatroom,
  disableSend,
  requestPermissions,
  uuid,
}) {
  if (disableSend) {
    return CHATROOM_SEND_PERMISSIONS_DISABLED
  }

  if (uuid) {
    const permissions = chatroom?.permissions

    return {
      canSendAiGeneratedMessage: permissions?.can_send_ai_generated_message,
      canSendMessage: permissions?.can_send_message,
      canSendTemplatedMessage: permissions?.can_send_templated_message,
    }
  }

  return {
    canSendAiGeneratedMessage:
      requestPermissions?.can_send_ai_generated_chat_message,
    canSendMessage: requestPermissions?.can_send_chat_message,
    canSendTemplatedMessage:
      requestPermissions?.can_send_templated_chat_message,
  }
}

export function getChatroomUnreadAnchorMessageUuid({ messages, unreadCount }) {
  if (!unreadCount || !messages || messages.length === 0) {
    return null
  }

  const startIndex = Math.max(0, messages.length - unreadCount)

  return messages[startIndex]?.uuid || null
}

export function getChatroomOldestMessageUuid(messages) {
  return messages?.[0]?.uuid || null
}

export function isChatroomMessageNewDay({
  currentMessage,
  previousMessage,
  timeZone,
}) {
  if (!currentMessage?.created) {
    return false
  }

  if (!previousMessage?.created) {
    return true
  }

  return !isSameDay(
    toUkTimezoneDate(currentMessage.created, timeZone),
    toUkTimezoneDate(previousMessage.created, timeZone)
  )
}

export function formatChatroomDateDivider({ date, t, timeZone }) {
  if (!date) {
    return EMPTY_STRING
  }

  const dateValue = new Date(date)

  if (Number.isNaN(dateValue.getTime())) {
    return EMPTY_STRING
  }

  const ukDate = toUkTimezoneDate(dateValue, timeZone)

  if (isToday(ukDate)) {
    return t('Chatroom.DateDividerTodayKey')
  }

  if (isYesterday(ukDate)) {
    return t('Chatroom.DateDividerYesterdayKey')
  }

  return isThisYear(ukDate)
    ? format(ukDate, DATE_DAY_MONTH_FORMAT)
    : format(ukDate, DATE_DAY_MONTH_YEAR_FORMAT)
}

export function formatChatroomStartedDate({ date, timeZone }) {
  if (!date) {
    return EMPTY_STRING
  }

  const dateValue = new Date(date)

  if (Number.isNaN(dateValue.getTime())) {
    return EMPTY_STRING
  }

  return format(
    toUkTimezoneDate(dateValue, timeZone),
    DATE_DAY_MONTH_YEAR_TIME_FORMAT
  )
}

// A deleted message is terminal: a late PATCH/GET response must not resurrect it.
export function applyChatroomMessageUpdate({ message, partial }) {
  return message.deleted && !partial.deleted
    ? message
    : { ...message, ...partial }
}

export function getChatroomMessageEditedAt(message) {
  return message?.edited || null
}

export function isChatroomMessageEdited(message) {
  return Boolean(getChatroomMessageEditedAt(message))
}

export function getChatroomComposeDraft(messageDraftRef) {
  return messageDraftRef?.current?.compose || null
}

export function saveChatroomComposeDraft(messageDraftRef, compose) {
  if (messageDraftRef) {
    messageDraftRef.current = { ...messageDraftRef.current, compose }
  }
}

export function getChatroomEditDraft(messageDraftRef) {
  return messageDraftRef?.current?.edit || null
}

export function saveChatroomEditDraft(messageDraftRef, edit) {
  if (messageDraftRef) {
    messageDraftRef.current = { ...messageDraftRef.current, edit }
  }
}
